import type { RealtimeEventFrame } from '@sentra/contracts';

import { adminKeys } from '@/api/admin-keys';
import { queryKeys } from '@/api/query-keys';
import type { InvalidationSink } from '@/features/realtime/coalesce';

/**
 * What each frame means to the cache, as a record total over the four event types.
 *
 * **Total, so a fifth frame cannot be added without an answer here.** That is the same discipline
 * the write-effects table is held to, and it matters more on this side: a frame nothing handles is
 * indistinguishable from a frame nobody sends.
 *
 * **Two of the four are handled and never arrive today, and that is deliberate rather than
 * aspirational.** `occupancy` and `anomaly` are published by `presence/service.ts` and
 * `presence/occupancy.ts`; `person-changed` and `alert` are declared in the contract, documented in
 * `INTEGRATION.md`, and published by nothing. Making them real needs a durable bridge from the
 * WORKER, which holds no hub by design — the alerts that matter are raised there, so publishing
 * from the api alone would cover some of them and look like it covered all, which is the failure
 * `realtime/events.ts` argues against for the resume path. What is written here is the correct
 * handling for the day a publisher exists, and a test drives it by feeding a frame.
 *
 * **Nothing here writes a figure.** Every handler invalidates and lets the route answer. The
 * occupancy frame carries `inside` and `outside` but not `overdue`, and the temptation is to write
 * the two counts and keep the third — which would put a fifth definition of "outside too long" in
 * the browser, against a rule (R352) that says there is exactly one and it must never grow a
 * replacement. The concrete cost of getting that wrong is a labelled safety figure claiming
 * residents are past their allowance when they are not. `invalidateQueries` refetches only ACTIVE
 * observers, so on a terminal that is not showing the card this costs nothing at all.
 *
 * **What a handler asks for goes through `coalesce.ts`, never straight to the cache.** A refetch is
 * asked with `invalidate`, which folds a burst of frames into one request per view per window; a
 * view whose READ writes an audit row is asked with `markStale`, which never refetches on a frame's
 * account — not now, and not when a hidden tab is shown again either.
 */
type FrameHandler = (sink: InvalidationSink, frame: RealtimeEventFrame) => void;

const HANDLERS: Record<RealtimeEventFrame['type'], FrameHandler> = {
  occupancy: (sink) => {
    // The snapshot, which carries `overdue` and the threshold it was measured against beside the
    // two counts this frame has.
    sink.invalidate(queryKeys.presenceCounts());
  },
  anomaly: (sink) => {
    // An override happened at some door in this building. What a screen here can say about it is
    // what the Live panel and the open alert set already say.
    sink.invalidate(adminKeys.panelPrefix('live'));
    sink.invalidate(adminKeys.alertsPrefix());
    // Marked stale rather than refetched, for the reason `person-changed` is below: every read of
    // the incident log writes a `presence.incident_review` audit row, so a frame from somebody
    // else's terminal must never make this one file one.
    sink.markStale(adminKeys.incidentsPrefix());
  },
  'person-changed': (sink, frame) => {
    if (frame.type !== 'person-changed') {
      return;
    }
    // Marked stale rather than refetched, twice over: the register is fed by a projection a second
    // or two behind, and `person.get` writes the `person.view_full` row AD-37 counts — so a frame
    // from somebody else's terminal must never make this one read a record.
    sink.markStale(queryKeys.personRegisterPrefix());
    sink.markStale(queryKeys.personDetail(frame.data.personId));
  },
  alert: (sink) => {
    sink.invalidate(adminKeys.alertsPrefix());
    sink.invalidate(adminKeys.panelPrefix('security'));
  },
};

/** Applies one frame. Never throws into the reader loop: a bad frame must not close the stream. */
export function applyRealtimeFrame(sink: InvalidationSink, frame: RealtimeEventFrame): void {
  HANDLERS[frame.type](sink, frame);
}
